import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { projectsService } from '../services/projects.service';

export interface TeamMember {
  userId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

export interface Project {
  id: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'on_hold' | 'completed' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  progress: number;
  deadline?: string;
  tags: string[];
  teamId: string;
  teamMembers?: TeamMember[];
  // Contract/Partnership references (for projects created from contracts)
  contractId?: string;
  partnershipId?: string;
  brand?: string;
  createdBy: string;
  archivedAt?: string;
  archivedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Subtask {
  title: string;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
}

export interface TimeEntry {
  userId: string;
  hours: number;
  description?: string;
  date: string;
}

export interface TaskAttachment {
  assetId?: string;
  fileName: string;
  fileUrl: string;
  fileType?: string;
  fileSize?: number;
  attachedBy?: string; // Set by backend
  attachedAt?: string; // Set by backend
}

export interface TaskDependency {
  taskId: string;
  type: 'blocks' | 'blocked_by' | 'related';
}

export interface CustomField {
  key: string;
  value: any;
}

export interface Task {
  id: string;
  projectId: string;
  contractId?: string;  // For tasks created from contracts
  deliverableId?: string;  // Links to specific deliverable
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'in_review' | 'done' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string;
  assignedBy?: string;
  assignedAt?: string;
  dueDate?: string;
  startDate?: string;
  completedAt?: string;
  completedBy?: string;
  subtasks?: Subtask[];
  estimatedHours?: number;
  timeEntries?: TimeEntry[];
  attachments?: TaskAttachment[];
  dependencies?: TaskDependency[];
  labels?: string[];
  customFields?: CustomField[];
  order: number;
  isArchived?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectFile {
  id: string;
  projectId: string;
  taskId?: string;
  assetId?: string;
  fileName: string;
  fileUrl: string;
  fileType?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnail?: string;
  version?: number;
  previousVersions?: Array<{
    version: number;
    fileUrl: string;
    uploadedAt: string;
    uploadedBy: string;
  }>;
  uploadedBy: string;
  isPublic?: boolean;
  sharedWith?: string[];
  tags?: string[];
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Timeline {
  id: string;
  projectId: string;
  milestone: string;
  description?: string;
  startDate: string;
  endDate: string;
  progress: number;
  linkedTasks?: string[];
  status?: 'upcoming' | 'in_progress' | 'completed' | 'overdue';
  assignedTo?: string[];
  color?: string;
  completedAt?: string;
  completedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommentAttachment {
  assetId?: string;
  fileName: string;
  fileUrl: string;
  fileType?: string;
}

export interface CommentReaction {
  userId: string;
  type: 'like' | 'love' | 'celebrate' | 'helpful';
}

export interface Comment {
  id: string;
  projectId: string;
  taskId?: string;
  author: string;
  text: string;
  mentions: string[];
  attachments?: CommentAttachment[];
  parentCommentId?: string;
  isEdited?: boolean;
  editedAt?: string;
  reactions?: CommentReaction[];
  isDeleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectData {
  title: string;
  description?: string;
  status?: Project['status'];
  priority?: Project['priority'];
  deadline?: string;
  tags?: string[];
  teamMembers?: TeamMember[];
}

export interface UpdateProjectData extends Partial<CreateProjectData> {
  id: string;
}

export interface CreateTaskData {
  projectId: string;
  title: string;
  description?: string;
  status?: Task['status'];
  priority?: Task['priority'];
  assignedTo?: string;
  dueDate?: string;
  startDate?: string;
  estimatedHours?: number;
  subtasks?: Subtask[];
  attachments?: TaskAttachment[];
  dependencies?: TaskDependency[];
  labels?: string[];
  customFields?: CustomField[];
  order?: number;
}

export interface UpdateTaskData {
  id: string;
  title?: string;
  description?: string;
  status?: Task['status'];
  priority?: Task['priority'];
  assignedTo?: string;
  dueDate?: string;
  startDate?: string;
  estimatedHours?: number;
  subtasks?: Subtask[];
  timeEntries?: TimeEntry[];
  attachments?: TaskAttachment[];
  dependencies?: TaskDependency[];
  labels?: string[];
  customFields?: CustomField[];
  order?: number;
  isArchived?: boolean;
}

export interface CreateTimelineData {
  projectId: string;
  milestone: string;
  description?: string;
  startDate: string;
  endDate: string;
  progress?: number;
  linkedTasks?: string[];
  status?: Timeline['status'];
  assignedTo?: string[];
  color?: string;
}

export interface CreateCommentData {
  projectId: string;
  taskId?: string;
  text: string;
  mentions?: string[];
  attachments?: CommentAttachment[];
  parentCommentId?: string;
}

interface ProjectsState {
  // State
  projects: Project[];
  tasks: Task[];
  files: ProjectFile[];
  timelines: Timeline[];
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  filterStatus: string;
  filterPriority: string;
  sortBy: 'recent' | 'title' | 'deadline' | 'progress';
  sortOrder: 'asc' | 'desc';
  viewingProject: Project | null;

  // Actions
  setSearchTerm: (term: string) => void;
  setFilterStatus: (status: string) => void;
  setFilterPriority: (priority: string) => void;
  setSortBy: (sort: ProjectsState['sortBy']) => void;
  setSortOrder: (order: ProjectsState['sortOrder']) => void;
  setViewingProject: (project: Project | null) => void;

  // Async Actions - Projects
  fetchProjects: () => Promise<void>;
  createProject: (data: CreateProjectData) => Promise<Project | null>;
  updateProject: (data: UpdateProjectData) => Promise<Project | null>;
  deleteProject: (id: string) => Promise<boolean>;

  // Async Actions - Tasks
  fetchTasks: (projectId: string) => Promise<void>;
  createTask: (data: CreateTaskData) => Promise<Task | null>;
  updateTask: (data: UpdateTaskData) => Promise<Task | null>;
  deleteTask: (id: string) => Promise<boolean>;

  // Async Actions - Files
  fetchFiles: (projectId: string) => Promise<void>;
  uploadFile: (projectId: string, file: File) => Promise<ProjectFile | null>;
  deleteFile: (id: string) => Promise<boolean>;

  // Async Actions - Timeline
  fetchTimelines: (projectId: string) => Promise<void>;
  createTimeline: (data: CreateTimelineData) => Promise<Timeline | null>;
  updateTimeline: (id: string, data: Partial<CreateTimelineData>) => Promise<Timeline | null>;
  deleteTimeline: (id: string) => Promise<boolean>;

  // Async Actions - Comments
  fetchComments: (projectId: string) => Promise<void>;
  createComment: (data: CreateCommentData) => Promise<Comment | null>;
  updateComment: (id: string, text: string) => Promise<Comment | null>;
  deleteComment: (id: string) => Promise<boolean>;
}

export const useProjectsStore = create<ProjectsState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        projects: [],
        tasks: [],
        files: [],
        timelines: [],
        comments: [],
        isLoading: false,
        error: null,
        searchTerm: '',
        filterStatus: 'all',
        filterPriority: 'all',
        sortBy: 'recent',
        sortOrder: 'desc',
        viewingProject: null,

        // Filter actions
        setSearchTerm: (term) => set({ searchTerm: term }),
        setFilterStatus: (status) => set({ filterStatus: status }),
        setFilterPriority: (priority) => set({ filterPriority: priority }),
        setSortBy: (sort) => set({ sortBy: sort }),
        setSortOrder: (order) => set({ sortOrder: order }),
        setViewingProject: (project) => set({ viewingProject: project }),

        // Fetch projects
        fetchProjects: async () => {
          set({ isLoading: true, error: null });
          try {
            const projects = await projectsService.getProjects();
            set({ projects, isLoading: false, error: null });
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch projects'
            });
          }
        },

        // Create project
        createProject: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const project = await projectsService.createProject(data);
            set((state) => ({
              projects: [project, ...state.projects],
              isLoading: false,
              error: null
            }));
            return project;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to create project'
            });
            return null;
          }
        },

        // Update project
        updateProject: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const updatedProject = await projectsService.updateProject(data);
            set((state) => ({
              projects: state.projects.map(p =>
                p.id === data.id ? updatedProject : p
              ),
              isLoading: false,
              error: null
            }));
            return updatedProject;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update project'
            });
            return null;
          }
        },

        // Delete project
        deleteProject: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await projectsService.deleteProject(id);
            set((state) => ({
              projects: state.projects.filter(p => p.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete project'
            });
            return false;
          }
        },

        // Fetch tasks
        fetchTasks: async (projectId) => {
          set({ isLoading: true, error: null });
          try {
            const tasks = await projectsService.getTasks(projectId);
            set((state) => ({
              tasks: [...state.tasks.filter(t => t.projectId !== projectId), ...tasks],
              isLoading: false,
              error: null
            }));
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch tasks'
            });
          }
        },

        // Create task
        createTask: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const task = await projectsService.createTask(data);
            set((state) => ({
              tasks: [...state.tasks, task],
              isLoading: false,
              error: null
            }));
            return task;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to create task'
            });
            return null;
          }
        },

        // Update task
        updateTask: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const updatedTask = await projectsService.updateTask(data);
            set((state) => ({
              tasks: state.tasks.map(t =>
                t.id === data.id ? updatedTask : t
              ),
              isLoading: false,
              error: null
            }));
            return updatedTask;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update task'
            });
            return null;
          }
        },

        // Delete task
        deleteTask: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await projectsService.deleteTask(id);
            set((state) => ({
              tasks: state.tasks.filter(t => t.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete task'
            });
            return false;
          }
        },

        // Fetch files
        fetchFiles: async (projectId) => {
          set({ isLoading: true, error: null });
          try {
            const files = await projectsService.getFiles(projectId);
            set((state) => ({
              files: [...state.files.filter(f => f.projectId !== projectId), ...files],
              isLoading: false,
              error: null
            }));
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch files'
            });
          }
        },

        // Upload file
        uploadFile: async (projectId, file) => {
          set({ isLoading: true, error: null });
          try {
            const projectFile = await projectsService.uploadFile(projectId, file);
            set((state) => ({
              files: [...state.files, projectFile],
              isLoading: false,
              error: null
            }));
            return projectFile;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to upload file'
            });
            return null;
          }
        },

        // Delete file
        deleteFile: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await projectsService.deleteFile(id);
            set((state) => ({
              files: state.files.filter(f => f.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete file'
            });
            return false;
          }
        },

        // Fetch timelines
        fetchTimelines: async (projectId) => {
          set({ isLoading: true, error: null });
          try {
            const timelines = await projectsService.getTimelines(projectId);
            set((state) => ({
              timelines: [...state.timelines.filter(t => t.projectId !== projectId), ...timelines],
              isLoading: false,
              error: null
            }));
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch timelines'
            });
          }
        },

        // Create timeline
        createTimeline: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const timeline = await projectsService.createTimeline(data);
            set((state) => ({
              timelines: [...state.timelines, timeline],
              isLoading: false,
              error: null
            }));
            return timeline;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to create timeline'
            });
            return null;
          }
        },

        // Update timeline
        updateTimeline: async (id, data) => {
          set({ isLoading: true, error: null });
          try {
            const updatedTimeline = await projectsService.updateTimeline(id, data);
            set((state) => ({
              timelines: state.timelines.map(t =>
                t.id === id ? updatedTimeline : t
              ),
              isLoading: false,
              error: null
            }));
            return updatedTimeline;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update timeline'
            });
            return null;
          }
        },

        // Delete timeline
        deleteTimeline: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await projectsService.deleteTimeline(id);
            set((state) => ({
              timelines: state.timelines.filter(t => t.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete timeline'
            });
            return false;
          }
        },

        // Fetch comments
        fetchComments: async (projectId) => {
          set({ isLoading: true, error: null });
          try {
            const comments = await projectsService.getComments(projectId);
            set((state) => ({
              comments: [...state.comments.filter(c => c.projectId !== projectId), ...comments],
              isLoading: false,
              error: null
            }));
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch comments'
            });
          }
        },

        // Create comment
        createComment: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const comment = await projectsService.createComment(data);
            set((state) => ({
              comments: [...state.comments, comment],
              isLoading: false,
              error: null
            }));
            return comment;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to create comment'
            });
            return null;
          }
        },

        // Update comment
        updateComment: async (id, text) => {
          set({ isLoading: true, error: null });
          try {
            const updatedComment = await projectsService.updateComment(id, text);
            set((state) => ({
              comments: state.comments.map(c =>
                c.id === id ? updatedComment : c
              ),
              isLoading: false,
              error: null
            }));
            return updatedComment;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update comment'
            });
            return null;
          }
        },

        // Delete comment
        deleteComment: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await projectsService.deleteComment(id);
            set((state) => ({
              comments: state.comments.filter(c => c.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete comment'
            });
            return false;
          }
        },
      }),
      {
        name: 'projects-storage',
        partialize: (state) => ({
          projects: state.projects,
          searchTerm: state.searchTerm,
          filterStatus: state.filterStatus,
          filterPriority: state.filterPriority,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
      }
    ),
    {
      name: 'projects-store',
    }
  )
);

